import React, { useEffect, useState, useRef } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  GlobalStyles,
  Container,
  Typography,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  MenuItem,
  Menu,
  Avatar,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Divider,
  Paper
} from "@mui/material";

import {
  SportsEsports as SportsEsportsIcon,
  CloudOutlined as CloudOutlinedIcon,
  Logout,
  Insights as InsightsIcon,
  Menu as MenuIcon,
  SmartToy,
} from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CallIcon from "@mui/icons-material/Call";
import CloseIcon from "@mui/icons-material/Close";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { getAssistants } from "../utils/AwsCalls";

// CSS imports
import "react-slideshow-image/dist/styles.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// AWS Amplify imports
import { signOut, fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { configureAutoTrack } from "aws-amplify/analytics";

// Local component imports
import AssistantSelection from "./AssistantSelection";
import MyLoader from "./MyLoader";
import DashboardDialog from "./DashboardDialog";
import DialogImage from "./DialogImage";
import Chat from "./Chat";
import { APP_NAME, ASSISTANT_NAME } from "../env";

// Architecture Balloon Component
const ArchitectureBalloon = ({ onClose }) => {
  const [opacity, setOpacity] = useState(0.75);

  useEffect(() => {
    // Start the fade effect after 4 seconds (to complete by 8 seconds)
    const fadeTimeout = setTimeout(() => {
      setOpacity(0);
    }, 4000);

    // Close the balloon after 8 seconds
    const closeTimeout = setTimeout(() => {
      onClose();
    }, 8000);

    // Clean up timeouts
    return () => {
      clearTimeout(fadeTimeout);
      clearTimeout(closeTimeout);
    };
  }, [onClose]);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        bottom: "2px",
        left: "54px", // Move it to the right of the cloud icon
        zIndex: 1200,
        py: 0.5,
        px: 1,
        width: 288,
        borderRadius: 3,
        opacity: opacity, // Use state-controlled opacity
        backgroundColor: "primary.main",
        boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
        animation: "pulse 2s infinite ease-in-out",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        "@keyframes pulse": {
          "0%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.05)" },
          "100%": { transform: "scale(1)" },
        },
        transition: "opacity 2s ease", // Slow 2-second fade transition
        "&:before": {
          // Changed from "after" to "before" and adjusted positioning
          content: '""',
          position: "absolute",
          width: 0,
          height: 0,
          top: "50%", // Position vertically in the middle
          left: "-15px", // Position arrow on the left side
          transform: "translateY(-50%)", // Center it vertically
          borderWidth: "8px 15px 8px 0", // Create a triangle pointing left
          borderStyle: "solid",
          borderColor: "transparent #E6549E transparent transparent", // Only right border has color
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="white" fontWeight="bold">
          View the AWS Architecture Diagram!
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            p: 0,
            border: "1px solid rgba(255, 255, 255, 0.3)",
            borderRadius: 1,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

configureAutoTrack({
  enable: true,
  type: "event",
});

// Home Button component shown only on specific pages
const HomeButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Only show on video games and post call pages
  const showHomeButton =
    location.pathname.includes("assistant/video-games-sales") ||
    location.pathname.includes("assistant/post-call-analysis");

  if (!showHomeButton) return null;

  return isSmallScreen ? (
    // Mobile version - just an icon
    <Link
      to="/"
      style={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      }}
      data-amplify-analytics-on="click"
      data-amplify-analytics-name="click"
      data-amplify-analytics-attrs="navigation:home"
    >
      <IconButton
        color="primary"
        onClick={() => navigate("/")}
        sx={{
          "&:hover": {
            color: (theme) => theme.palette.primary.light,
          },
        }}
        data-amplify-analytics-on="click"
        data-amplify-analytics-name="click"
        data-amplify-analytics-attrs="navigation:home-button"
      >
        <ArrowBackIosIcon />
      </IconButton>
    </Link>
  ) : (
    // Desktop version - button with text
    <Link
      to="/"
      style={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      }}
      data-amplify-analytics-on="click"
      data-amplify-analytics-name="click"
      data-amplify-analytics-attrs="navigation:home"
    >
      <IconButton
        color="primary"
        onClick={() => navigate("/")}
        sx={{
          "&:hover": {
            color: (theme) => theme.palette.primary.light,
          },
        }}
        data-amplify-analytics-on="click"
        data-amplify-analytics-name="click"
        data-amplify-analytics-attrs="navigation:home-button"
      >
        <ArrowBackIosIcon />
      </IconButton>
    </Link>
  );
};

// Route observer component to handle route changes
const RouteChangeObserver = ({ setAnswers, setDashboardEnabled }) => {
  const location = useLocation();
  useEffect(() => {
    // Clear answers when route changes
    setAnswers([]);
    setDashboardEnabled(false);
    console.log("Route changed. Clearing answers array.");
  }, [location.pathname, setAnswers, setDashboardEnabled]);

  return null;
};

// Icon selector component based on current route
const AppIconSelector = () => {
  const location = useLocation();

  if (location.pathname === "/") {
    return <SmartToy color="primary" />;
  } else if (location.pathname.includes("assistant/video-games-sales")) {
    return <SportsEsportsIcon color="primary" />;
  } else if (location.pathname.includes("assistant/post-call-analysis")) {
    return <CallIcon color="primary" />;
  } else {
    return <SmartToy color="primary" />;
  }
};

// Assistant title component based on current route
const AssistantTitleSelector = ({ isMobile }) => {
  const location = useLocation();

  if (location.pathname === "/") {
    return APP_NAME;
  } else if (location.pathname.includes("assistant/video-games-sales")) {
    if (isMobile) {
      return ASSISTANT_NAME;
    } else {
      return ASSISTANT_NAME + " for Video Games Sales";
    }
  } else if (location.pathname.includes("assistant/post-call-analysis")) {
    if (isMobile) {
      return ASSISTANT_NAME;
    } else {
      return ASSISTANT_NAME + " for Post-Call Analysis";
    }
  } else {
    return APP_NAME;
  }
};

// Create a responsive theme
const createResponsiveTheme = () => {
  return createTheme({
    palette: {
      mode: "light",
      text: {
        primary: "#202328",
        secondary: "#707070",
      },
      primary: {
        main: "#E6549E",
        light: "#F178B6",
        dark: "#C2337C",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#28CE27",
        light: "#56DE55",
        dark: "#1E9D1D",
        contrastText: "#FFFFFF",
      },
      background: {
        default: "#FFFFFF",
        paper: "#FFFFFF",
      },
      copy: {
        main: "#7F7F7F",
        dark: "#5F5F5F",
      },
      error: {
        main: "#FF3D00",
      },
      warning: {
        main: "#FFB400",
      },
      info: {
        main: "#2196F3",
      },
      success: {
        main: "#4CAF50",
      },
      divider: "rgba(0, 0, 0, 0.12)",
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        fontWeight: 500,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: "#7F7F7F",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: "#56DE55",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            minWidth: 240,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            padding: "8px 16px",
          },
        },
      },
    },
  });
};

// Main App Layout
const LayoutApp = () => {
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [dashboardEnabled, setDashboardEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDashboard, setOpenDashboard] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [language, setLanguage] = useState("en");
  const [anchorEl, setAnchorEl] = useState(null);
  const openUserMenu = Boolean(anchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showBalloon, setShowBalloon] = useState(true); // New state for balloon visibility

  // Create theme only once
  const theme = useRef(createResponsiveTheme());

  // Media queries for responsive design
  const isSmallScreen = useMediaQuery(theme.current.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.current.breakpoints.down("md"));

  const handleChangeLanguage = (event) => {
    setLanguage(event.target.value);
  };

  const handleClickOpen = () => {
    setOpen(true);
    setShowBalloon(false); // Hide balloon when architecture is clicked
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseBalloon = () => {
    setShowBalloon(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const effectRan = React.useRef(false);
  useEffect(() => {
    if (!effectRan.current) {
      console.log("effect applied - only on the FIRST mount");

      const fetchUserData = async () => {
        console.log("Layout");
        try {
          const currentUser = await getCurrentUser();
          console.log(currentUser);
          setUserName(
            currentUser.signInDetails.loginId
              .split("@")[0]
              .charAt(0)
              .toUpperCase() +
              currentUser.signInDetails.loginId
                .split("@")[0]
                .slice(1)
                .toLowerCase()
          );
          setEmail(currentUser.signInDetails.loginId);
          const userAttributes = await fetchUserAttributes();
          if ("name" in userAttributes) {
            setUserName(userAttributes.name);
          }
          console.log(userAttributes);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      };

      const fetchAssistantsData = async () => {
        try {
          const assistantsData = await getAssistants();
          console.log("Assistants loaded:", assistantsData);
          setAssistants(assistantsData);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching assistants:", error);
        }
      };

      // Execute both fetch functions in parallel
      Promise.all([fetchUserData(), fetchAssistantsData()])
        .catch(console.error)
        .finally(() => {
          console.log("complete loading");
        });
    }

    return () => (effectRan.current = true);
  }, []);

  async function handleSignOut() {
    try {
      await signOut();
    } catch (error) {
      console.log("error signing out: ", error);
    }
  }

  const handleOpenUserMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleClickOpenDashboard = () => {
    setShowDashboard(true);
    setOpenDashboard(true);
  };

  const handleCloseDashboard = () => {
    setOpenDashboard(false);
    setTimeout(() => {
      setShowDashboard(false);
    }, "1000");
  };

  // Language selection component to be used in multiple places
  const LanguageSelection = ({ horizontal = false }) => (
    <Box>
      <Typography sx={{ mb: 1 }}>Sample Questions Language</Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: horizontal ? "row" : "column",
        }}
      >
        <IconButton
          size="small"
          onClick={() => setLanguage("en")}
          sx={{
            color: language === "en" ? "primary.main" : "text.secondary",
            border: language === "en" ? 1 : 0,
            borderColor: "primary.main",
            px: 1,
          }}
        >
          EN
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setLanguage("es")}
          sx={{
            color: language === "es" ? "primary.main" : "text.secondary",
            border: language === "es" ? 1 : 0,
            borderColor: "primary.main",
            px: 1,
          }}
        >
          ES
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setLanguage("pt")}
          sx={{
            color: language === "pt" ? "primary.main" : "text.secondary",
            border: language === "pt" ? 1 : 0,
            borderColor: "primary.main",
            px: 1,
          }}
        >
          PT
        </IconButton>
      </Box>
    </Box>
  );

  // Mobile navigation drawer content
  const drawerContent = (
    <Box
      sx={{ width: 368 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/">
            <ListItemIcon>
              <SmartToy color="primary" />
            </ListItemIcon>
            <ListItemText primary={APP_NAME} />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />

      <List>
        <ListItem disablePadding>
          <ListItemButton
            disabled={!dashboardEnabled}
            onClick={handleClickOpenDashboard}
          >
            <ListItemIcon>
              <InsightsIcon />
            </ListItemIcon>
            <ListItemText primary="Generate BI Report" />
          </ListItemButton>
        </ListItem>

        <Divider />

        <ListItem disablePadding>
          <Box sx={{ mt: "auto", p: 2 }}>
            <LanguageSelection horizontal={true} />
          </Box>
        </ListItem>

        <Divider />

        <ListItem disablePadding>
          <ListItemButton onClick={handleSignOut}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </ListItem>
        <Divider />
      </List>

      <Box sx={{ mt: "auto", p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <img
            src="/images/Powered-By_logo-horiz_RGB.png"
            height={24}
            alt="Powered By AWS"
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme.current}>
      <Router>
        <RouteChangeObserver
          setAnswers={setAnswers}
          setDashboardEnabled={setDashboardEnabled}
        />
        <GlobalStyles
          styles={{
            ul: { margin: 0, padding: 0, listStyle: "none" },
            body: { overflowX: "hidden" },
          }}
        />
        <CssBaseline />

        <AppBar
          color="default"
          elevation={0}
          sx={(theme) => ({
            backdropFilter: "blur(20px)",
            boxShadow: "0 4px 30px rgba(255, 255, 255, 0.3)",
            borderBottom: "1px solid",
            borderColor: "rgba(255, 255, 255, 0.4)",
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 100%)",
          })}
        >
          {isSmallScreen ? (
            // Mobile view
            <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                >
                  <MenuIcon />
                </IconButton>
                
                {/* Home button for mobile */}
                <HomeButton />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AppIconSelector />
                <Typography
                  color="text.primary"
                  variant="h6"
                  component="div"
                  noWrap
                  sx={{ pl: 1 }}
                >
                  <AssistantTitleSelector isMobile={true} />
                </Typography>
              </Box>

              <IconButton
                size="small"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
              >
                <Avatar
                  sx={(theme) => ({
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.secondary.light,
                  })}
                >
                  {userName.substring(0, 1).toUpperCase()}
                </Avatar>
              </IconButton>
            </Toolbar>
          ) : (
            // Desktop view
            <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* Home button for desktop */}
                <HomeButton />

                <AppIconSelector />
                <Typography
                  color="text.primary"
                  variant="h6"
                  component="div"
                  noWrap
                  sx={{ pl: 1 }}
                >
                  <AssistantTitleSelector isMobile={false} />
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ pr: 1 }}>{userName}</Typography>

                <IconButton
                  size="small"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenUserMenu}
                >
                  <Avatar
                    sx={(theme) => ({
                      width: 32,
                      height: 32,
                      bgcolor: theme.palette.secondary.light,
                    })}
                  >
                    {userName.substring(0, 1).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Box>
            </Toolbar>
          )}
        </AppBar>

        {/* Mobile Navigation Drawer */}
        <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
          {drawerContent}
        </Drawer>

        {/* User Menu (shared between mobile and desktop) */}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={openUserMenu}
          onClose={handleCloseUserMenu}
          onClick={handleCloseUserMenu}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 0.6,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 16,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={handleClickOpenDashboard}
            disabled={!dashboardEnabled}
            data-amplify-analytics-on="click"
            data-amplify-analytics-name="click"
            data-amplify-analytics-attrs="button:generate-dashboard"
          >
            <ListItemIcon>
              <InsightsIcon fontSize="small" />
            </ListItemIcon>
            Generate BI Report
          </MenuItem>

          <Divider />

          <MenuItem
            disableRipple
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Box>
              <LanguageSelection horizontal={true} />
            </Box>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={handleSignOut}
            data-amplify-analytics-on="click"
            data-amplify-analytics-name="click"
            data-amplify-analytics-attrs="button:sign-out"
          >
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>

        {/* Main Content */}
        <Container maxWidth="xl" component="main">
          {loading ? (
            <MyLoader
              loading={loading}
              message="Loading your Data Analyst Assistant..."
            />
          ) : (
            <Routes>
              <Route
                path="/"
                element={
                  <AssistantSelection
                    assistants={assistants}
                    setAssistants={setAssistants}
                  />
                }
              />
              {assistants.map((assistant, index) => (
                <Route
                  key={index}
                  path={"assistant/" + assistant.id}
                  element={
                    <Chat
                      userName={userName}
                      answers={answers}
                      setAnswers={setAnswers}
                      setDashboardEnabled={setDashboardEnabled}
                      language={language}
                      assistant={assistant}
                    />
                  }
                />
              ))}
              <Route
                path="*"
                element={<AssistantSelection assistants={assistants} />}
              />
            </Routes>
          )}
        </Container>

        {/* Footer for Desktop */}
        <Box
          sx={{
            position: "fixed",
            width: "100%",
            bottom: 0,
            pb: "12px",
            textAlign: "center",
            background:
              "linear-gradient(to bottom, rgba(249, 249, 249, 0) 0%, rgba(249, 249, 249, 0.5) 60%, rgba(249, 249, 249, 1) 100%)",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "none", md: "block", pt: 1 },
            }}
          >
            <Typography
              color="copy.main"
              variant="caption"
              sx={{
                textShadow:
                  "2px 2px 4px #F9F9F9, -2px -2px 4px #F9F9F9, 2px -2px 4px #F9F9F9, -2px 2px 4px #F9F9F9",
              }}
            >
              &copy;{new Date().getFullYear()}, Amazon Web Services, Inc. or its
              affiliates. All rights reserved.
            </Typography>
          </Box>

          {/* Footer for Mobile */}
          <Box
            sx={{
              display: { sm: "block", md: "none" },
            }}
          >
            <Typography
              color="copy.main"
              variant="caption"
              sx={{
                textShadow:
                  "2px 2px 4px #F9F9F9, -2px -2px 4px #F9F9F9, 2px -2px 4px #F9F9F9, -2px 2px 4px #F9F9F9",
              }}
            >
              &copy;{new Date().getFullYear()}, AWS. All rights reserved.
            </Typography>
          </Box>

          {/* AWS Logo - Desktop */}
          <Box
            sx={{
              position: "fixed",
              bottom: 6,
              left: 24,
              display: {
                xs: "none",
                sm: "flex",
                md: "flex",
                lg: "flex",
                xl: "flex",
              },
              zIndex: 2,
            }}
          >
            {/* Cloud icon with notification badge */}
            <IconButton
              size="small"
              data-amplify-analytics-on="click"
              data-amplify-analytics-name="click"
              data-amplify-analytics-attrs="button:cloud"
              aria-label="AWS Architecture Diagram"
              onClick={handleClickOpen}
              sx={{
                animation: showBalloon ? "pulse-icon 1.5s infinite" : "none",
                "@keyframes pulse-icon": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
                "&:hover": {
                  color: (theme) => theme.palette.primary.light,
                },
              }}
            >
              <CloudOutlinedIcon />
            </IconButton>

            {/* Balloon showing only if showBalloon is true */}
            {showBalloon && (
              <ArchitectureBalloon onClose={handleCloseBalloon} />
            )}
          </Box>

          {/* Architecture Button */}
          <Box
            sx={{
              position: "fixed",
              display: { xs: "none", sm: "block" },
              bottom: 0,
              right: 24,
              zIndex: 2,
            }}
          >
            <img
              src="/images/Powered-By_logo-horiz_RGB.png"
              height={24}
              alt="Powered By AWS"
            />
          </Box>
        </Box>

        <DialogImage
          open={open}
          handleClose={handleClose}
          imagePath="/images/gen-ai-assistant-diagram.png"
          title="Multi-Domain Data Analyst Assistant Architecture with InlineAgents"
        />

        {showDashboard && (
          <DashboardDialog
            open={openDashboard}
            handleClose={handleCloseDashboard}
            answers={answers}
          />
        )}
      </Router>
    </ThemeProvider>
  );
};

export default LayoutApp;
